import React from "react";
import {
    Link,
    Routes,
    Route,
    useNavigate,
    useLocation,
} from "react-router-dom";
import SocialConnections from "./Connections/SocialConnections";
import PersonalInformation from "./PersonalInformation";
import Subscription from "./Subscription/Subscription";
import {
    BellDot,
    CreditCard,
    Settings as SettingsIcon,
    Share2,
    User,
    Users,
    Palette,
    Building2,
    Clapperboard,
    Store,
} from "lucide-react";
import { Card, CardContent } from "../misc/Card";
import { useIsMobile } from "../../hooks/useIsMobile";
import ManageTeam from "./ManageTeam";
import NotificationPreferences from "./NotificationPreferences";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import ApplicationSettings from "./ApplicationSettings/ApplicationSettings";
import MlsDetails from "./MlsDetails/MlsDetails";
import { BsWrench } from "react-icons/bs";
import BrandingSettings from "./Branding/BrandingSettings";
import AutoVideoPreferences from "./AutoVideoPreferences/AutoVideoPreferences";
import Autopilot from "./Autopilot/Autopilot";
import ContentCleanup from "./ContentCleanup/ContentCleanup";
import Commercial from "./Commercial/Commercial";
import MyNicheSettings from "./MyNiche/MyNicheSettings";
import { Eraser, Rocket, Target } from "lucide-react";

const settingsSections = [
    {
        id: "autopilot",
        title: "Auto Pilot",
        component: Autopilot,
        icon: Rocket,
        description:
            "Automatic listing campaigns — set it once and Marabot markets every listing for you",
    },
    {
        id: "my-niche",
        title: "My Niche",
        component: MyNicheSettings,
        icon: Target,
        description: "Who you serve and where - powers My Niche Video ideas and scripts",
    },
    {
        id: "commercial",
        title: "Commercial",
        component: Commercial,
        icon: Store,
        description:
            "Preferences for office, retail, industrial, land, and other non-residential listings",
    },
    {
        id: "connections",
        title: "Connections",
        component: SocialConnections,
        icon: Share2,
        description: "Manage your social media connections",
    },
    {
        id: "notification-preferences",
        title: "Notification Preferences",
        component: NotificationPreferences,
        icon: BellDot,
        description: "Manage your notification preferences",
    },
    {
        id: "manage-team",
        title: "Manage Team",
        component: ManageTeam,
        icon: Users,
        description: "Manage your team members",
    },
    {
        id: "personal-information",
        title: "Personal Information",
        component: PersonalInformation,
        icon: User,
        description: "Update your personal information",
    },
    {
        id: "mls-details",
        title: "MLS Agent Profile",
        component: MlsDetails,
        icon: Building2,
        description: "View your MLS agent details and control auto-syncing of active listings",
    },
    {
        id: "subscription",
        title: "Subscription",
        component: Subscription,
        icon: CreditCard,
        description: "Manage your subscription plan and billing",
    },
    {
        id: "application-settings",
        title: "Application Settings",
        component: ApplicationSettings,
        icon: BsWrench,
        description: "Manage Marabot settings",
    },
    {
        id: "content-cleanup",
        title: "Content Cleanup",
        component: ContentCleanup,
        icon: Eraser,
        description:
            "Automatically remove old My Brand and Lead Generation content",
    },
    {
        id: "branding",
        title: "Branding",
        component: BrandingSettings,
        icon: Palette,
        description: "Manage your branding settings",
    },
    {
        id: "auto-video-preferences",
        title: "Listing Video Settings",
        component: AutoVideoPreferences,
        icon: Clapperboard,
        description: "Set the logo, colors, templates & music for auto-generated videos",
    },
];

const Settings = () => {
    const user = useSelector((state: RootState) => state.auth.user);
    const isMobile = useIsMobile(900);
    const navigate = useNavigate();
    const location = useLocation();

    // Determine if a specific section is active based on the URL
    const pathParts = location.pathname.split("/");
    const activeSectionId = pathParts[pathParts.length - 1];

    // Redirect to the main settings page if the route is invalid
    React.useEffect(() => {
        if (
            activeSectionId &&
            !settingsSections.some((section) => section.id === activeSectionId)
        ) {
            navigate("/settings", { replace: true });
        }
    }, [activeSectionId, navigate]);

    return (
        <div
            className={`container mx-auto px-4 py-6 sm:py-10 ${
                isMobile ? "bg-white min-h-full" : "bg-background h-auto"
            }`}
        >
            <Card className="w-full max-w-4xl mx-auto">
                <CardContent className="p-0">
                    <div className="p-4 sm:p-6">
                        <div>
                            <h1 className="flex items-center text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-primary">
                                <SettingsIcon className="h-6 w-6 sm:h-8 sm:w-8 mr-3 sm:mr-4 text-secondary" />
                                Settings
                            </h1>
                            <div
                                className={`grid gap-4 ${
                                    isMobile ? "grid-cols-1" : "grid-cols-2"
                                }`}
                            >
                                {settingsSections.map((section) => {
                                    // Conditional rendering based on user role
                                    if (
                                        section.id === "manage-team" &&
                                        user?.role !== "TEAM_ADMIN"
                                    ) {
                                        return null;
                                    }
                                    if (
                                        section.id === "subscription" &&
                                        user?.role === "TEAM_USER"
                                    ) {
                                        return null;
                                    }
                                    // MB-1314 (§O5): auto-video is MLS-only, so a
                                    // non-realtor (Rentcast-only) user has nothing to
                                    // configure here.
                                    if (
                                        section.id === "auto-video-preferences" &&
                                        !user?.mlsApproved
                                    ) {
                                        return null;
                                    }
                                    // MB-1481: commercial listings are MLS-sync-only,
                                    // so a Rentcast-only user has nothing to configure
                                    // here either.
                                    if (
                                        section.id === "commercial" &&
                                        !user?.mlsApproved
                                    ) {
                                        return null;
                                    }
                                    return (
                                        <Card
                                            id={section.id}
                                            key={section.id}
                                            className="cursor-pointer hover:bg-secondary hover:text-white text-neutral-500 transition-colors"
                                        >
                                            <Link
                                                to={`/settings/${section.id}`}
                                                className="flex items-center p-4 sm:p-6"
                                            >
                                                <section.icon className="h-6 w-6 sm:h-8 sm:w-8 mr-3 sm:mr-4 text-primary" />
                                                <div>
                                                    <h2 className="text-base sm:text-lg font-semibold text-primary">
                                                        {section.title}
                                                    </h2>
                                                    <p className="text-xs sm:text-sm">
                                                        {section.description}
                                                    </p>
                                                </div>
                                            </Link>
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    {/* Define nested routes for settings sections */}
                    <Routes>
                        {settingsSections.map((section) => {
                            // Conditional rendering based on user role
                            if (
                                section.id === "manage-team" &&
                                user?.role !== "TEAM_ADMIN"
                            ) {
                                return null;
                            }
                            if (
                                section.id === "subscription" &&
                                user?.role === "TEAM_USER"
                            ) {
                                return null;
                            }
                            // MB-1314 (§O5): auto-video is MLS-only — keep the route
                            // unreachable for non-realtors, not just the card hidden.
                            if (
                                section.id === "auto-video-preferences" &&
                                !user?.mlsApproved
                            ) {
                                return null;
                            }
                            // MB-1481: commercial is MLS-sync-only — keep the route
                            // unreachable for non-realtors, not just the card hidden.
                            if (
                                section.id === "commercial" &&
                                !user?.mlsApproved
                            ) {
                                return null;
                            }
                            return (
                                <Route
                                    key={section.id}
                                    path={section.id}
                                    element={<section.component />}
                                />
                            );
                        })}
                    </Routes>
                </CardContent>
            </Card>
        </div>
    );
};

export default Settings;
